/**
 * Shared date helpers for the WorkshopsCalendar card + grid.
 *
 * TWO distinct kinds of date live here, and they must not be confused:
 *
 *  - CALENDAR SCAFFOLD (the month/week/anchor/cell/today dates) is UTC-anchored
 *    calendar math, so day/column placement agrees with the Sunday-anchored
 *    `weekStartIso` the dashboards pass around. These are plain calendar dates,
 *    never a workshop time, so `getUTC*` here is correct and deliberate.
 *
 *  - WORKSHOP TIMES are placed and displayed from the chip's VENUE-LOCAL wall
 *    clock (`WorkshopChip.localDate`, a naive ISO with no `Z`), never the
 *    absolute `date` instant (BON-386 / design D10). `chipDayKey` buckets by it
 *    and `fmtTime` labels it — both via `utils/venueTime`, with a null-safe
 *    fallback to the UTC instant so a row with no stored local time still shows.
 */
import { venueWallClock } from '../../../utils/venueTime';

export const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

export const MONTHS_FULL = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/** Sunday-first labels; rotate by `weekStartsOn` for display. */
export const DOW_LABELS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'] as const;

/** 0 = Sunday … 6 = Saturday, matching `Date.getUTCDay()`. */
export type WeekStartsOn = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/**
 * Sunday. Every other calendar in the app (venue closures, the workshop picker,
 * the shared CalendarView) is Sunday-first, and so is the business week the
 * dashboard DTOs compute against (BON-533).
 */
export const DEFAULT_WEEK_STARTS_ON: WeekStartsOn = 0;

/**
 * The viewer's own calendar day, as a UTC scaffold date. Scaffold math reads
 * dates with `getUTC*`, so a raw instant would put a US viewer at 8pm into
 * tomorrow's (UTC) day — this copies the browser-local Y/M/D instead, which is
 * the same day the today marker highlights.
 */
export function viewerDayUtc(d: Date = new Date()): Date {
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
}

export function startOfDayUtc(d: Date): Date {
  const r = new Date(d);
  r.setUTCHours(0, 0, 0, 0);
  return r;
}

export function addDaysUtc(d: Date, n: number): Date {
  const r = new Date(d);
  r.setUTCDate(r.getUTCDate() + n);
  return r;
}

export function addMonthsUtc(d: Date, n: number): Date {
  const r = new Date(d);
  r.setUTCMonth(r.getUTCMonth() + n);
  return r;
}

/** Back up to the first day of the week containing `d`. */
export function startOfWeekUtc(d: Date, weekStartsOn: WeekStartsOn): Date {
  const r = startOfDayUtc(d);
  r.setUTCDate(r.getUTCDate() - ((r.getUTCDay() - weekStartsOn + 7) % 7));
  return r;
}

export function sameDayUtc(a: Date, b: Date): boolean {
  return (
    a.getUTCFullYear() === b.getUTCFullYear() &&
    a.getUTCMonth() === b.getUTCMonth() &&
    a.getUTCDate() === b.getUTCDate()
  );
}

/**
 * Calendar-day key `YYYY-MM-DD` of a chip, from its VENUE-LOCAL wall clock
 * (`localDate`) — the day the workshop happens at the venue. Falls back to the
 * UTC instant's date when no local time is stored. Both branches slice the
 * date off the ISO string with no zone re-anchoring (mirrors
 * WorkshopCalendarPicker.bucketByDay).
 */
export function chipDayKey(chip: { date: string; localDate?: string | null }): string {
  return (chip.localDate ?? chip.date).slice(0, 10);
}

/** Calendar-day key `YYYY-MM-DD` of a UTC-anchored SCAFFOLD date (cell/anchor).
 *  Scaffold only — never call this on a workshop time. */
export function utcDayKey(d: Date): string {
  const iso = d.toISOString();
  return iso.slice(0, 10);
}

/** `YYYY-MM` of a UTC-anchored SCAFFOLD date, for month-membership tests. */
export function utcYearMonth(d: Date): string {
  return d.toISOString().slice(0, 7);
}

/**
 * The clock label ("6:30 pm", "12:00 pm") for a chip's VENUE-LOCAL wall clock.
 * Reads the wall clock via `utils/venueTime` (no zone shift), never the
 * absolute instant. Empty string when the time is missing/unparseable.
 */
export function fmtTime(iso: string | null | undefined): string {
  const wc = venueWallClock(iso);
  if (!wc) return '';
  const ampm = wc.hours >= 12 ? 'pm' : 'am';
  const h12 = wc.hours % 12 === 0 ? 12 : wc.hours % 12;
  return `${h12}:${String(wc.minutes).padStart(2, '0')} ${ampm}`;
}
